import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, IconButton, Alert, Tooltip, InputAdornment, useTheme, useMediaQuery,
  FormControlLabel, Switch, Divider
} from '@mui/material';
import { Add, Edit, Block, Search, CheckCircle, DeleteForever } from '@mui/icons-material';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SlideTransition from '../../components/common/SlideTransition';
import DevBadge from '../../components/common/DevBadge';

const ROLES = ['teamlead', 'clientlead', 'member', 'superadmin'];
const ROLE_LABELS = { superadmin: 'Super Admin', teamlead: 'Líder de Equipo', clientlead: 'Líder Cliente', member: 'Miembro' };

export default function UsersManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', lastName: '', phone: '', role: 'member' });
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState({ open: false, userId: null });
  const [purgeConfirm, setPurgeConfirm] = useState({ open: false, userId: null, userEmail: '' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const load = async () => {
    try {
      const res = await usersAPI.getAll({ search });
      setUsers(res.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search]);

  const openCreate = () => {
    setEditUser(null);
    setForm({ email: '', name: '', lastName: '', phone: '', role: 'member' });
    setSendWelcomeEmail(true);
    setError('');
    setOpenDialog(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ email: user.email, name: user.name || '', lastName: user.lastName || '', phone: user.phone || '', role: user.role });
    setError('');
    setOpenDialog(true);
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      if (editUser) {
        await usersAPI.update(editUser._id, { name: form.name, lastName: form.lastName, phone: form.phone, role: form.role });
      } else {
        await usersAPI.create({ ...form, sendWelcomeEmail });
      }
      setOpenDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      await usersAPI.delete(userId);
      setConfirm({ open: false, userId: null });
      load();
    } catch { }
  };

  const handlePurge = async (userId) => {
    try {
      await usersAPI.purge(userId);
      setPurgeConfirm({ open: false, userId: null, userEmail: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar usuario');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <DevBadge id="page:admin-usuarios" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Gestión de Usuarios</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Nuevo Usuario</Button>
      </Box>

      <Card>
        <Box sx={{ p: 2 }}>
          <TextField
            size="small" placeholder="Buscar por nombre o email..."
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 320 }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Último Acceso</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(user => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {user.name ? `${user.name} ${user.lastName || ''}`.trim() : user.email}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                  <TableCell>
                    <Chip label={ROLE_LABELS[user.role]} size="small"
                      color={user.role === 'superadmin' ? 'error' : user.role === 'teamlead' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status === 'active' ? 'Activo' : user.status === 'invited' ? 'Invitado' : 'Inactivo'}
                      size="small" variant="outlined"
                      color={user.status === 'active' ? 'success' : user.status === 'invited' ? 'warning' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('es') : 'Nunca'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                      <Tooltip title="Editar">
                        <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => openEdit(user)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      {user.status !== 'inactive' && me?._id !== user._id && (
                        <Tooltip title="Desactivar">
                          <IconButton size="small" color="error" sx={{ borderRadius: '5px', bgcolor: 'rgba(211,47,47,0.08)', '&:hover': { bgcolor: 'rgba(211,47,47,0.18)' } }}
                            onClick={() => setConfirm({ open: true, userId: user._id })}>
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {me?._id !== user._id && (
                        <Tooltip title="Eliminar permanentemente (con todos sus datos)">
                          <IconButton size="small" color="error" sx={{ borderRadius: '5px', bgcolor: 'rgba(211,47,47,0.08)', '&:hover': { bgcolor: 'rgba(211,47,47,0.18)' } }}
                            onClick={() => setPurgeConfirm({ open: true, userId: user._id, userEmail: user.email })}>
                            <DeleteForever fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>No se encontraron usuarios</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          {editUser ? 'Editar Usuario' : 'Nuevo Usuario'}
          <DevBadge id="modal:usuario" inline />
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Email" value={form.email} disabled={!!editUser}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth />
            <Box display="flex" gap={2}>
              <TextField label="Nombre" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
              <TextField label="Apellido" value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} fullWidth />
            </Box>
            <TextField label="Teléfono" value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth
              placeholder="+58 412 000 0000"
              error={!!(form.phone && (form.phone.length < 7 || form.phone.length > 15))}
              helperText={(form.phone && form.phone.length > 0 && form.phone.length < 7) ? 'Mínimo 7 caracteres.' : (form.phone && form.phone.length > 15) ? 'Máximo 15 caracteres.' : ''}
              inputProps={{ maxLength: 15 }} />
            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select value={form.role} label="Rol" onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>)}
              </Select>
            </FormControl>
            {!editUser && (
              <>
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={sendWelcomeEmail}
                      onChange={e => setSendWelcomeEmail(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Enviar correo de bienvenida</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Notifica al usuario con sus datos de acceso y un enlace al login.
                      </Typography>
                    </Box>
                  }
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm.open}
        title="Desactivar Usuario"
        message="¿Estás seguro de desactivar este usuario? Ya no podrá acceder al sistema."
        confirmLabel="Desactivar"
        onConfirm={() => handleDeactivate(confirm.userId)}
        onCancel={() => setConfirm({ open: false, userId: null })}
      />

      <ConfirmDialog
        open={purgeConfirm.open}
        title="⚠️ Eliminar usuario permanentemente"
        message={`¿Eliminar permanentemente a "${purgeConfirm.userEmail}"?\n\nEsta acción es IRREVERSIBLE y borrará también todas sus actividades registradas, bloqueos y demás datos asociados.`}
        confirmLabel="Eliminar permanentemente"
        onConfirm={() => handlePurge(purgeConfirm.userId)}
        onCancel={() => setPurgeConfirm({ open: false, userId: null, userEmail: '' })}
      />
    </Box>
  );
}
